/**
 * Componente de tabla para el listado de merchants.
 *
 * @taskId T468
 * @see functional/Specs/specs_cycle_4.md HU-26-FE §Pantalla 1
 */

import { Eye, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationLink,
} from '@flexibilitypay/ui';

import type { Merchant, MerchantStatus } from '../types/merchant.types';

// ─── Status Badge ───────────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: MerchantStatus;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const colorMap: Record<MerchantStatus, string> = {
    ACTIVE: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    INACTIVE: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    CLOSED: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  };

  const labelMap: Record<MerchantStatus, string> = {
    ACTIVE: 'Activo',
    INACTIVE: 'Inactivo',
    CLOSED: 'Baja',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colorMap[status]}`}
    >
      {labelMap[status]}
    </span>
  );
}

// ─── Avatar con iniciales ───────────────────────────────────────────────────

interface MerchantAvatarProps {
  razonSocial: string;
}

function MerchantAvatar({ razonSocial }: MerchantAvatarProps) {
  const initials = razonSocial
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-medium text-primary">
      {initials}
    </div>
  );
}

// ─── Formateo de CUIT ───────────────────────────────────────────────────────

function formatCuit(cuit: string): string {
  // Si ya está formateado, devolverlo
  if (cuit.includes('-')) return cuit;
  // Formatear: XX-XXXXXXXX-X
  return `${cuit.slice(0, 2)}-${cuit.slice(2, 10)}-${cuit.slice(10)}`;
}

// ─── Props ──────────────────────────────────────────────────────────────────

export interface MerchantListTableProps {
  merchants: Merchant[];
  isLoading?: boolean;
  onView360: (merchant: Merchant) => void;
  onEdit: (merchant: Merchant) => void;
  onRowClick?: (merchant: Merchant) => void;
  // Paginación
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

// ─── Skeleton de carga ──────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <div className="space-y-1">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-24" />
            </div>
          </TableCell>
          <TableCell>
            <Skeleton className="h-6 w-16 rounded-full" />
          </TableCell>
          <TableCell>
            <div className="flex gap-1">
              <Skeleton className="h-8 w-8 rounded" />
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ─── Estado vacío ───────────────────────────────────────────────────────────

function EmptyState() {
  const { t } = useTranslation('merchants');

  return (
    <TableRow>
      <TableCell colSpan={5} className="h-32 text-center">
        <p className="text-muted-foreground">
          {t('list.empty', 'No se encontraron merchants con los filtros aplicados.')}
        </p>
      </TableCell>
    </TableRow>
  );
}

// ─── Componente principal ───────────────────────────────────────────────────

export function MerchantListTable({
  merchants,
  isLoading = false,
  onView360,
  onEdit,
  onRowClick,
  currentPage,
  totalPages,
  onPageChange,
}: MerchantListTableProps) {
  const { t } = useTranslation('merchants');

  // Generar array de páginas para paginación
  const getPageNumbers = () => {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible);
    start = Math.max(0, end - maxVisible);

    for (let i = start; i < end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[300px]">{t('list.columns.merchant', 'Merchant')}</TableHead>
              <TableHead>{t('list.columns.id', 'ID')}</TableHead>
              <TableHead>{t('list.columns.contact', 'Contacto')}</TableHead>
              <TableHead>{t('list.columns.status', 'Estado')}</TableHead>
              <TableHead className="w-[100px]">{t('list.columns.actions', 'Acciones')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableSkeleton />
            ) : merchants.length === 0 ? (
              <EmptyState />
            ) : (
              merchants.map((merchant) => (
                <TableRow
                  key={merchant.id}
                  className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}
                  onClick={() => onRowClick?.(merchant)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <MerchantAvatar razonSocial={merchant.razonSocial} />
                      <div>
                        <p className="font-medium">{merchant.razonSocial}</p>
                        <p className="text-sm text-muted-foreground">
                          {formatCuit(merchant.cuit)}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{merchant.id}</TableCell>
                  <TableCell>
                    <div>
                      <p className="text-sm">{merchant.email}</p>
                      <p className="text-sm text-muted-foreground">{merchant.telefono}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={merchant.estado} />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onView360(merchant);
                        }}
                        title={t('list.actions.view360', 'Ver vista 360')}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(merchant);
                        }}
                        title={t('list.actions.edit', 'Editar')}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => onPageChange(Math.max(0, currentPage - 1))}
                aria-disabled={currentPage === 0}
                className={currentPage === 0 ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>

            {getPageNumbers().map((page) => (
              <PaginationItem key={page}>
                <PaginationLink
                  onClick={() => onPageChange(page)}
                  isActive={page === currentPage}
                >
                  {page + 1}
                </PaginationLink>
              </PaginationItem>
            ))}

            <PaginationItem>
              <PaginationNext
                onClick={() => onPageChange(Math.min(totalPages - 1, currentPage + 1))}
                aria-disabled={currentPage >= totalPages - 1}
                className={currentPage >= totalPages - 1 ? 'pointer-events-none opacity-50' : ''}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
